<template>
    <div class="flex h-screen flex-col items-center justify-center px-6 bg-background">
        <div class="flex flex-col items-center text-center max-w-md">
            <p class="text-[10rem] leading-none font-semibold text-new-neutral-200 tracking-tight">
                {{ code }}
            </p>

            <h1 class="mt-4 text-2xl font-semibold text-foreground">
                {{ title }}
            </h1>

            <p class="mt-3 text-sm text-muted-foreground">
                {{ description }}
            </p>

            <div class="mt-8 flex items-center gap-3">
                <Button variant="outline" @click="goBack">
                    <ArrowLeft class="size-4" />
                    Go back
                </Button>
                <Button @click="goHome">
                    Take me home
                </Button>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, PropType } from 'vue';
import { router } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import { ArrowLeft } from 'lucide-vue-next';
import Bugsnag from '@bugsnag/js';
import BlankLayout from '@/Layouts/Blank.vue';
import { Button } from '@/components/ui/button';

defineOptions({ layout: BlankLayout });

const props = defineProps({
    status: {
        type: Number as PropType<number>,
        default: 404,
    },
});

const code = computed(() => String(props.status));

const title = computed(
    () =>
        ({
            503: 'Down for maintenance',
            500: 'Something went wrong',
            429: 'Hold on a moment',
            405: 'Method not allowed',
            404: 'Page not found',
            403: 'Access denied',
        }[props.status] ?? 'Oops!'),
);

const description = computed(
    () =>
        ({
            503: 'Sorry, we’re doing some maintenance. Please check back soon.',
            500: 'The server failed to handle your request. Please try again in a moment.',
            429: 'You’re making too many requests. Please try again shortly.',
            405: 'This action isn’t supported here.',
            404: 'We couldn’t find the page you’re looking for.',
            403: 'You don’t have access to this page.',
        }[props.status] ?? 'Something unexpected happened.'),
);

const goBack = () => {
    if (document.referrer && document.referrer.startsWith(window.location.origin)) {
        window.history.back();
    } else {
        router.visit(route('home'));
    }
};

const goHome = () => {
    router.visit(route('home'));
};

onMounted(() => {
    if ([405, 500, 503].includes(props.status)) {
        // @ts-ignore — Bugsnag accepts a number here
        Bugsnag.notify(props.status);
    }
});
</script>
